import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import constant from '../constant';
import {
  getSessionStorage,
  getStorage,
  removeSessionStorage,
  sessionStorageUpdateEventName,
  setStorage,
} from '../utils';

const NotificationContext = createContext();

const getUserIdFromSession = () => {
  try {
    const encoded = getSessionStorage(constant.keys.userDetails);
    if (encoded) {
      const parsed = JSON.parse(atob(encoded));
      const id = Number(parsed?.id ?? parsed?.ID ?? parsed?.userId ?? parsed?.USER_ID);
      if (Number.isFinite(id)) return id;
    }
  } catch (error) {
    // Fall through to token decoding.
  }

  try {
    const token = String(getSessionStorage(constant.keys.userToken) || '').trim();
    if (!token) return null;

    const tokenParts = token.split('.');
    if (tokenParts.length < 2) return null;

    const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const normalizedPayload = base64Payload.padEnd(base64Payload.length + (4 - (base64Payload.length % 4 || 4)) % 4, '=');
    const payload = JSON.parse(atob(normalizedPayload));
    const id = Number(payload?.USER_ID ?? payload?.userId ?? payload?.id ?? payload?.sub);
    return Number.isFinite(id) ? id : null;
  } catch (error) {
    return null;
  }
};

const getNotificationStoreKey = (userId) => `${constant.keys.notificationFeed}.${userId || 'guest'}`;

const safeNotificationList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      id: item?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type: item?.type || 'system',
      title: item?.title || 'Notification',
      message: item?.message || '',
      link: item?.link || '',
      meta: item?.meta || {},
      createdAt: item?.createdAt || new Date().toISOString(),
      read: Boolean(item?.read),
      dedupeKey: item?.dedupeKey || null,
    }))
    .slice(0, 100);
};

export const NotificationProvider = ({ children }) => {
  const [userId, setUserId] = useState(() => getUserIdFromSession());
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const syncUserId = () => {
      const nextUserId = getUserIdFromSession();
      setUserId((prevUserId) => (prevUserId === nextUserId ? prevUserId : nextUserId));
    };

    syncUserId();

    if (typeof window === 'undefined') return undefined;

    const handleSessionStorageUpdate = (event) => {
      const key = String(event?.detail?.key || '');
      if (!key || key === constant.keys.userDetails || key === constant.keys.userToken) {
        syncUserId();
      }
    };

    window.addEventListener(sessionStorageUpdateEventName, handleSessionStorageUpdate);
    window.addEventListener('focus', syncUserId);
    document.addEventListener('visibilitychange', syncUserId);

    return () => {
      window.removeEventListener(sessionStorageUpdateEventName, handleSessionStorageUpdate);
      window.removeEventListener('focus', syncUserId);
      document.removeEventListener('visibilitychange', syncUserId);
    };
  }, []);

  useEffect(() => {
    const storageKey = getNotificationStoreKey(userId);
    const localStored = getStorage(storageKey);

    // Migrate old session-scoped notifications to persistent local storage.
    if (localStored !== null) {
      setNotifications(safeNotificationList(localStored));
      return;
    }

    const sessionStored = safeNotificationList(getSessionStorage(storageKey));
    setNotifications(sessionStored);
    if (sessionStored.length > 0) {
      setStorage(storageKey, sessionStored);
      removeSessionStorage(storageKey);
    }
  }, [userId]);

  useEffect(() => {
    setStorage(getNotificationStoreKey(userId), notifications);
  }, [notifications, userId]);

  const pushNotification = useCallback((payload = {}) => {
    const createdAt = payload.createdAt || new Date().toISOString();
    const id = payload.id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const dedupeKey = payload.dedupeKey || null;

    setNotifications((prev) => {
      if (dedupeKey) {
        const nowTs = new Date(createdAt).getTime();
        const hasDuplicate = prev.some((item) => {
          if (item.dedupeKey !== dedupeKey) return false;
          const ts = new Date(item.createdAt).getTime();
          if (Number.isNaN(ts) || Number.isNaN(nowTs)) return true;
          return Math.abs(nowTs - ts) <= 5 * 60 * 1000;
        });
        if (hasDuplicate) return prev;
      }

      const next = [{
        id,
        type: payload.type || 'system',
        title: payload.title || 'Notification',
        message: payload.message || '',
        link: payload.link || '',
        meta: payload.meta || {},
        createdAt,
        read: false,
        dedupeKey,
      }, ...prev];

      return next.slice(0, 100);
    });
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => prev.map((item) => (
      item.id === id ? { ...item, read: true } : item
    )));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  }, []);

  const clearNotifications = useCallback(() => {
    removeSessionStorage(getNotificationStoreKey(userId));
    setNotifications([]);
  }, [userId]);

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        pushNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        userId,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
